"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useDisplayLocale } from "@/lib/displayLocale";
import { HELP_ARTICLES, HELP_CATEGORIES } from "@/lib/helpArticles";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";

/**
 * EPIC-024 STORY-665 — help center index.
 *
 * Articles are static (in-bundle, see lib/helpArticles.ts). Closed-
 * beta scope: read + browse-by-category. Search lands at STORY-669.
 *
 * Surface chrome (title, subtitle, CTAs) flows through react-i18next
 * like the rest of the codebase. Article body content lives in
 * helpArticles.ts as `{en, pt}` records — those still go through
 * useDisplayLocale because they're per-record bilingual fields, not
 * named keys.
 */
export default function HelpIndexPage() {
  const { t } = useTranslation();
  const locale = useDisplayLocale();

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />

        <main className="min-w-0">
          <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <h1 className="text-[22px] font-bold tracking-tight">
                {t("helpCenter.title")}
              </h1>
              <p className="mt-1 max-w-2xl text-[13px] text-[var(--color-text3)]">
                {t("helpCenter.subtitle")}
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                href="/help/new"
                className="rounded-md border border-[var(--color-brand)] bg-[var(--color-brand-tint)] px-3 py-1.5 text-[12px] font-bold text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-white"
              >
                {t("helpCenter.fileTicket")}
              </Link>
              <Link
                href="/help/mine"
                className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)]"
              >
                {t("helpCenter.myTickets")}
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            {HELP_CATEGORIES.map((cat) => {
              const articles = HELP_ARTICLES.filter(
                (a) => a.category === cat.id,
              );
              if (articles.length === 0) return null;
              return (
                <section key={cat.id}>
                  <h2 className="mb-2 flex items-center gap-1.5 text-[14px] font-bold">
                    <cat.Icon
                      size={14}
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                    {cat.label[locale]}
                  </h2>
                  <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {articles.map((a) => (
                      <li key={a.slug}>
                        <Link
                          href={`/help/${a.slug}`}
                          className="block rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-colors hover:border-[var(--color-border2)]"
                        >
                          <p className="text-[13px] font-bold">
                            {a.title[locale]}
                          </p>
                          <p className="mt-1 text-[12px] text-[var(--color-text3)]">
                            {a.description[locale]}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </main>
      </div>
    </>
  );
}
